import { API_BASE_URL } from './api';
import { isTokenExpired } from '../utils/jwtUtils';

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface UserDto {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  createdAt: string;
  lastLoginAt?: string;
  hasSuuntoConnection: boolean;
  lastSyncAt?: string;
  emailNotificationsEnabled?: boolean;
  serviceReminderDays?: number;
  isAdmin?: boolean;
  /** Feature access flags. Used to hide tabs the user cannot open; the backend enforces access independently. */
  hasChatAccess?: boolean;
  hasFlashcardAccess?: boolean;
  hasApneaAccess?: boolean;
  /** Set while an email change is awaiting confirmation at the new address */
  pendingEmail?: string | null;
}

export interface AuthResponse {
  success: boolean;
  token?: string;
  user?: UserDto;
  error?: string;
}

export interface UpdateProfileRequest {
  firstName: string;
  lastName: string;
  email: string;
  /** Required by the API only when the email address is changing */
  currentPassword?: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface UpdateProfileResponse extends AuthResponse {
  /** True when the new address still needs to be confirmed via the emailed link */
  emailConfirmationPending?: boolean;
  pendingEmail?: string;
}

export interface UserLookupResult {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
}

class AuthService {
  private token: string | null = null;
  private user: UserDto | null = null;
  private listeners = new Set<() => void>();

  constructor() {
    // Load token and user from localStorage on initialization
    this.token = localStorage.getItem('authToken');
    if (this.token && isTokenExpired(this.token)) {
      // Drop stale sessions up front instead of surfacing 401s mid-use
      this.token = null;
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
      return;
    }
    const storedUser = localStorage.getItem('authUser');
    if (storedUser) {
      try {
        this.user = JSON.parse(storedUser);
      } catch (e) {
        console.error('Failed to parse stored user:', e);
        localStorage.removeItem('authUser');
      }
    }
  }

  async register(request: RegisterRequest): Promise<AuthResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        this.setAuthData(data.token, data.user);
      }

      return data;
    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: 'Network error. Please try again.',
      };
    }
  }

  async login(request: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        this.setAuthData(data.token, data.user);
      }

      return data;
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: 'Network error. Please try again.',
      };
    }
  }

  async forgotPassword(email: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) {
        return { success: false, error: 'Request failed. Please try again.' };
      }
      return { success: true };
    } catch (error) {
      console.error('Forgot password error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  async resetPassword(email: string, token: string, newPassword: string): Promise<AuthResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, token, newPassword }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        this.setAuthData(data.token, data.user);
      }

      return data;
    } catch (error) {
      console.error('Reset password error:', error);
      return {
        success: false,
        error: 'Network error. Please try again.',
      };
    }
  }

  async updateProfile(request: UpdateProfileRequest): Promise<UpdateProfileResponse> {
    if (!this.token) {
      return { success: false, error: 'You must be signed in to update your profile.' };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders(),
        },
        body: JSON.stringify(request),
      });

      const data = await response.json();

      if (response.ok && data.success && data.token && data.user) {
        // Email and name are baked into the JWT, so swap in the reissued token
        this.setAuthData(data.token, data.user);
      }

      return data;
    } catch (error) {
      console.error('Update profile error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  async confirmEmailChange(token: string): Promise<AuthResponse> {
    if (!this.token) {
      return { success: false, error: 'Sign in to confirm your new email address.' };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/confirm-email-change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders(),
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (response.ok && data.success && data.token && data.user) {
        // The email claim changed, so the previous token is stale
        this.setAuthData(data.token, data.user);
      }

      return data;
    } catch (error) {
      console.error('Confirm email change error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  async cancelEmailChange(): Promise<AuthResponse> {
    if (!this.token) {
      return { success: false, error: 'You must be signed in to do that.' };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/cancel-email-change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders(),
        },
      });

      const data = await response.json();

      if (response.ok && data.success && data.token && data.user) {
        this.setAuthData(data.token, data.user);
      }

      return data;
    } catch (error) {
      console.error('Cancel email change error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  async changePassword(request: ChangePasswordRequest): Promise<AuthResponse> {
    if (!this.token) {
      return { success: false, error: 'You must be signed in to change your password.' };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders(),
        },
        body: JSON.stringify(request),
      });

      const data = await response.json();

      if (response.ok && data.success && data.token && data.user) {
        this.setAuthData(data.token, data.user);
      }

      return data;
    } catch (error) {
      console.error('Change password error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  async searchUsers(query: string): Promise<UserLookupResult[]> {
    if (!this.token) {
      return [];
    }

    const normalizedQuery = query.trim();
    if (normalizedQuery.length < 2) {
      return [];
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/users/search?query=${encodeURIComponent(normalizedQuery)}`, {
        headers: {
          ...this.getAuthHeaders(),
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to search users');
      }

      return await response.json();
    } catch (error) {
      console.error('User search error:', error);
      return [];
    }
  }

  async getUsersByIds(userIds: string[]): Promise<UserLookupResult[]> {
    if (!this.token) {
      return [];
    }

    const normalizedIds = Array.from(new Set(userIds.map((userId) => userId.trim()).filter(Boolean))).slice(0, 25);
    if (normalizedIds.length === 0) {
      return [];
    }

    const searchParams = new URLSearchParams();
    normalizedIds.forEach((userId) => searchParams.append('userIds', userId));

    try {
      const response = await fetch(`${API_BASE_URL}/auth/users/by-ids?${searchParams.toString()}`, {
        headers: {
          ...this.getAuthHeaders(),
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to load users');
      }

      return await response.json();
    } catch (error) {
      console.error('User lookup by ID error:', error);
      return [];
    }
  }

  /**
   * Re-fetch the current user from the server so cached data (e.g. isAdmin)
   * never goes stale, regardless of how or when the user logged in.
   */
  async refreshUser(): Promise<UserDto | null> {
    if (!this.token) {
      return null;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: {
          ...this.getAuthHeaders(),
        },
      });

      if (response.status === 401) {
        // Token is no longer valid - clear stale auth state
        this.logout();
        return null;
      }

      if (!response.ok) {
        return this.user;
      }

      const data = await response.json();
      if (data.user) {
        this.updateUser(data.user);
      }
      return this.user;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return this.user;
    }
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener());
  }

  logout(): void {
    this.token = null;
    this.user = null;
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    this.notifyListeners();
  }

  private setAuthData(token: string, user: UserDto): void {
    this.token = token;
    this.user = user;
    localStorage.setItem('authToken', token);
    localStorage.setItem('authUser', JSON.stringify(user));
    this.notifyListeners();
  }

  getToken(): string | null {
    return this.token;
  }

  getUser(): UserDto | null {
    return this.user;
  }

  isAuthenticated(): boolean {
    return this.token !== null && this.user !== null;
  }

  getAuthHeaders(): HeadersInit {
    if (!this.token) {
      return {};
    }
    return {
      'Authorization': `Bearer ${this.token}`,
    };
  }

  updateUser(user: UserDto): void {
    this.user = user;
    localStorage.setItem('authUser', JSON.stringify(user));
    this.notifyListeners();
  }
}

export const authService = new AuthService();
